/**
 * EmployeeSkillsBridgerV2.jsx
 *
 * Pure render layer — all state, API calls, and derived data live in
 * useEmployeeSkillsBridger. Sub-components and columns are each in their
 * own files. Constants are in EmployeeSkillsBridgerV2.constants.js.
 */

import React, { useMemo, useEffect } from "react";
import { Typography, Pagination } from "antd";
import SbButton from "uiCore/SbButton/SbButton";
import SbSwitch from "uiCore/SbSwitch/SbSwitch";
import SbTable from "uiCore/SbTable/SbTable";
import SbCard from "uiCore/SbCard/SbCard";
import SbEmpty from "uiCore/SbEmpty/SbEmpty";
import SbSpin from "uiCore/SbSpin/SbSpin";
import SbTour from "uiCore/SbTour/SbTour";
import buildTourSteps from "uiCore/SbTour/buildTourSteps";
import { CheckCircleOutlined } from "uiCore/SbIcons/SbIcons";
import SkillsBridgersIcon from "Components/SBLayoutIcon/SkillsBridgersIcon";
import CoreMarkdownEditor from "Components/SbMarkdownEditor/CoreMarkdownEditor";
import { VIEWMODE } from "constants/markdown.constants";
import SbSbProficiencyDrawer from "Components/SbSbProficiencyDrawer/SbSbProficiencyDrawer";
import SbSbViewSourcesDrawerV2 from "Components/SbSbViewSourcesDrawerV2/SbSbViewSourcesDrawerV2";
import ProgressCircleCardV2 from "Components/ProgressCircleCard/ProgressCircleCardV2";
import { DEEP_LINK_ACTIONS } from "Utils/deepLinks";
import useDeepLinkAction from "Hooks/useDeepLinkAction";
import {
  PageContainerStyled,
  HeaderLeftStyled,
  HeaderRightStyled,
  HeadingRowStyled,
  ActionRowStyled,
} from "styles/common.styles";
import {
  EmployeePageHeaderStyled,
  EmployeeTableWrapperStyled,
  ContentGridStyled,
  JD_PANEL_CARD_STYLE,
  JD_PANEL_CARD_STYLES,
  ScaleFooterStyled,
  ScaleHeadingStyled,
  ScaleItemsRowStyled,
  ScaleItemTextStyled,
  PaginationFooterStyled,
  SuccessAlertStyled,
  HeaderDescriptionStyled,
  TourTargetStyled,
} from "./EmployeeSkillsBridgerV2.styles";
import useEmployeeSkillsBridger from "./useEmployeeSkillsBridger";
import buildColumns from "./buildColumns";
import ReasonModal from "./components/ReasonModal";
import ScaleDetailsModal from "./components/ScaleDetailsModal";
import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
import {
  JdModalTitleStyled,
  JdFileIconStyled,
  JdCloseIconStyled,
} from "./components/JdDetailsModal/JdDetailsModal.styles";
import {
  VIEW_MODE_JD,
  VIEW_MODE_ADDITIONAL,
  SKILL_LEVEL,
  PAGE_SIZE_OPTIONS,
  ALERT_TYPE_SUCCESS,
  TEXT,
  TOUR_STEPS,
} from "./EmployeeSkillsBridgerV2.constants";
import {
  BUTTON_TYPE_PRIMARY,
  BUTTON_TYPE_DEFAULT,
  TYPE_SECONDARY,
  SIZE_SMALL,
} from "constants/ui.constants";

const { Title, Text } = Typography;

const ANALYTICS_FROM = "skills_bridger_employee";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.SKILLS_BRIDGER.EMPLOYEE_VIEW_OPENED;

const EmployeeSkillsBridgerV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const {
    email,
    accessToken,
    isDrawerOpen,
    toggleDrawer,
    drawerTypeRef,
    loading,
    allSkills,
    paginatedSkills,
    sbiscore,
    scaleData,
    jdMarkdown,
    jdPanel,
    showJdPanel,
    viewMode,
    setViewMode,
    currentPage,
    pageSize,
    viewSuccess,
    viewToaster,
    setViewToaster,
    handleViewJdClick,
    handleJdClose,
    isSubmitting,
    selfReportedProficiencies,
    invalidInput,
    activeReasonSkillId,
    activeReasonSkill,
    reasonText,
    handleSelfReportedChange,
    handleReasonTextChange,
    handleProficiencyClose,
    handleReasonsSubmit,
    handleSourcesClick,
    handleSourcesDrawerClose,
    handlePaginationChange,
    tourOpen,
    tourRunId,
    changeTourStep,
    closeTour,
    finishTour,
    tourStep,
    tourTargetRefs,
    drawerTourActive,
    handleDrawerTourComplete,
  } = useEmployeeSkillsBridger();


  // Turn the module's step list into AntD tour steps + button renderers: spotlight
  // steps target a live ref and render a TourStep; the Welcome/Finish greetings
  // render a centered SbTourMessage whose primary button advances (or finishes)
  // the tour. See uiCore/SbTour/buildTourSteps.
  const {
    steps: tourSteps,
    actionsRender: renderTourActions,
    indicatorsRender: renderTourIndicators,
  } = buildTourSteps(TOUR_STEPS, {
    resolveTarget: (step) => tourTargetRefs[step.key]?.current,
    greeting: {
      icon: (step, isLast) => (isLast ? <CheckCircleOutlined /> : <SkillsBridgersIcon />),
      onPrimary: (step, index, isLast) => (isLast ? finishTour() : changeTourStep(index + 1)),
      onSecondary: () => closeTour(),
    },
  });

  const columns = useMemo(
    () =>
      buildColumns({
        viewMode,
        selfReportedProficiencies,
        invalidInput,
        handleSelfReportedChange,
        selfReportedHeaderRef: tourTargetRefs.selfReported,
      }),
    [viewMode, selfReportedProficiencies, invalidInput, handleSelfReportedChange, tourTargetRefs.selfReported]
  );

  return (
    <PageContainerStyled>

      {/* ── Fixed Header ── */}
      <EmployeePageHeaderStyled>
        <HeaderLeftStyled>
          <HeadingRowStyled>
            <Title level={5}>{TEXT.PAGE_TITLE_PREFIX} {TEXT.VIEW_TITLE_JD}</Title>
            <TourTargetStyled ref={tourTargetRefs.toggle}>
              <SbSwitch
                checked={viewMode === VIEW_MODE_ADDITIONAL}
                onChange={(checked) => setViewMode(checked ? VIEW_MODE_ADDITIONAL : VIEW_MODE_JD)}
              />
            </TourTargetStyled>
            <Title level={5}>{TEXT.VIEW_TITLE_ADDITIONAL}</Title>
          </HeadingRowStyled>
          <HeaderDescriptionStyled>
            <Text type={TYPE_SECONDARY}>{TEXT.DESCRIPTION}</Text>
          </HeaderDescriptionStyled>
        </HeaderLeftStyled>

        <div ref={tourTargetRefs.gauge}>
          <ProgressCircleCardV2
            score={sbiscore}
            title={TEXT.SCORE_CARD_TITLE}
            subtitle={TEXT.SCORE_CARD_SUBTITLE}
          />
        </div>

        <HeaderRightStyled>
          <ActionRowStyled>
            <Text type={TYPE_SECONDARY}>{TEXT.SOURCES_LABEL}</Text>
            <TourTargetStyled ref={tourTargetRefs.sourceFiles}>
              <SbButton buttonType={BUTTON_TYPE_PRIMARY} className="action-btn" onClick={handleSourcesClick}>
                {TEXT.BTN_SOURCE_FILES}
              </SbButton>
            </TourTargetStyled>
          </ActionRowStyled>
          {/* <ActionRowStyled>
            <Text type={TYPE_SECONDARY}>{TEXT.LINKEDIN_LABEL}</Text>
            <SbButton buttonType={BUTTON_TYPE_PRIMARY} className="action-btn" onClick={handleSourcesClick}>
              {TEXT.BTN_CONNECT_LINKEDIN}
            </SbButton>
          </ActionRowStyled> */}
          {jdPanel.assigned && (
            <ActionRowStyled>
              <Text type={TYPE_SECONDARY}>{jdPanel.visible ? TEXT.CLOSE_JD_LABEL : TEXT.VIEW_JD_LABEL}</Text>
              <SbButton
                buttonType={BUTTON_TYPE_PRIMARY}
                className="action-btn"
                onClick={handleViewJdClick}
              >
                {jdPanel.visible ? TEXT.BTN_CLOSE_JD : TEXT.BTN_VIEW_JD}
              </SbButton>
            </ActionRowStyled>
          )}
        </HeaderRightStyled>
      </EmployeePageHeaderStyled>

      {/* ── Scrollable Table + Job Description panel ── */}
      <ContentGridStyled $showJd={showJdPanel}>
        <EmployeeTableWrapperStyled ref={tourTargetRefs.table}>
          <SbTable
            columns={columns}
            dataSource={paginatedSkills}
            rowKey="skill_id"
            loading={loading}
            sticky
            pagination={false}
          />
        </EmployeeTableWrapperStyled>

        {showJdPanel && (
          <SbCard
            style={JD_PANEL_CARD_STYLE}
            styles={JD_PANEL_CARD_STYLES}
            title={
              <JdModalTitleStyled>
                <JdFileIconStyled />
                {TEXT.JD_MODAL_TITLE}
              </JdModalTitleStyled>
            }
            extra={<JdCloseIconStyled onClick={handleJdClose} />}
          >
            <SbSpin spinning={loading}>
              {jdMarkdown ? (
                <CoreMarkdownEditor key={jdMarkdown} initialMarkdown={jdMarkdown} viewMode={VIEWMODE.VIEW} />
              ) : (
                <SbEmpty description={TEXT.JD_MODAL_EMPTY} />
              )}
            </SbSpin>
          </SbCard>
        )}
      </ContentGridStyled>

      {/* ── Skills Proficiency Scale ── */}
      {scaleData?.proficiency_scale && (
        <ScaleFooterStyled>
          <ScaleHeadingStyled>{TEXT.SCALE_HEADING}</ScaleHeadingStyled>
          <ScaleItemsRowStyled>
            {scaleData.proficiency_scale.map((field, i) => (
              <ScaleItemTextStyled key={i}>
                {field.level}{TEXT.SCALE_SEPARATOR}<strong>{field.name}</strong>
              </ScaleItemTextStyled>
            ))}
            <TourTargetStyled ref={tourTargetRefs.scale}>
              <SbButton buttonType={BUTTON_TYPE_DEFAULT} onClick={() => setViewToaster(true)}>
                {TEXT.BTN_VIEW_DETAILS}
              </SbButton>
            </TourTargetStyled>
          </ScaleItemsRowStyled>
        </ScaleFooterStyled>
      )}

      {/* ── Pagination Footer ── */}
      <PaginationFooterStyled>
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={allSkills.length}
          onChange={handlePaginationChange}
          showSizeChanger
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          size={SIZE_SMALL}
          showTotal={(total, range) => (
            <div className="paic-pagination-total">
              {TEXT.PAGINATION_SHOWING} <strong>{pageSize}</strong> {TEXT.PAGINATION_ITEMS_PER_PAGE}&nbsp;
              ({range[1] - range[0] + 1} of {total} skills)
            </div>
          )}
        />
      </PaginationFooterStyled>

      {/* ── Modals ── */}
      <ReasonModal
        skillId={activeReasonSkillId}
        skill={activeReasonSkill}
        reasonText={reasonText[activeReasonSkillId] || ""}
        onCancel={() => handleProficiencyClose(activeReasonSkillId)}
        onOk={handleReasonsSubmit}
        onReasonChange={handleReasonTextChange}
        isSubmitting={isSubmitting}
      />

      <ScaleDetailsModal
        open={viewToaster}
        scaleData={scaleData}
        onClose={() => setViewToaster(false)}
      />

      {/* ── Guided tour (Welcome + Finish are centered steps, no modal) ── */}
      {/* keyed by tourRunId so each start remounts fresh — prevents the
          reopen-during-close-animation freeze (card visible but buttons dead). */}
      <SbTour
        key={`${tourRunId}-${showJdPanel ? "jd-open" : "jd-closed"}`}
        open={tourOpen}
        current={tourStep}
        onChange={changeTourStep}
        steps={tourSteps}
        onClose={closeTour}
        actionsRender={renderTourActions}
        indicatorsRender={renderTourIndicators}
      />

      {/* ── Drawers ── */}
      {isDrawerOpen && drawerTypeRef.current === "proficiency" && (
        <SbSbProficiencyDrawer
          isDrawerOpen={isDrawerOpen}
          toggleDrawer={toggleDrawer}
          skillLevel={SKILL_LEVEL}
          accessToken={accessToken}
        />
      )}
      {isDrawerOpen && drawerTypeRef.current === "sources" && (
        <SbSbViewSourcesDrawerV2
          isDrawerOpen={isDrawerOpen}
          toggleDrawer={handleSourcesDrawerClose}
          accessToken={accessToken}
          email={email}
          active={drawerTourActive}
          onTourComplete={handleDrawerTourComplete}
        />
      )}

      {/* ── Success alert ── */}
      {viewSuccess && (
        <SuccessAlertStyled
          type={ALERT_TYPE_SUCCESS}
          description={TEXT.SUCCESS_ALERT_TITLE}
          showIcon
        />
      )}

    </PageContainerStyled>
  );
};

export default EmployeeSkillsBridgerV2;
